/* eslint-disable react-compiler/react-compiler */
import { useListJobTypesQuery } from '@/common/infrastructure/gql/generated/schema';
import { AutocompleteOption, useAutocomplete } from '@/common/presentation/hooks/useAutocomplete';

/**
 * Type representing a job type from the GraphQL query result
 */
type JobType = NonNullable<NonNullable<NonNullable<ReturnType<typeof useListJobTypesQuery>['data']>['jobTypes']>['items']>[number];

export interface UseListJobTypesAutocompleteParams {
  searchTerm?: string;
  debounceMs?: number;
  onJobTypeSelect?: (option: AutocompleteOption) => void;
  skipQuery?: boolean;
}

/**
 * Hook for implementing job type autocomplete functionality
 *
 * @param params - Configuration object
 * @param params.searchTerm - The current search term to filter job types by (default: '')
 * @param params.debounceMs - Debounce time in milliseconds for search term updates (default: 300ms)
 * @param params.onJobTypeSelect - Optional callback when a job type is selected
 * @param params.skipQuery - When true, the query is not executed (default: false)
 * @returns Object containing job type options, loading state, error state, and filter function
 */
export const useListJobTypesAutocomplete = ({
  searchTerm = '',
  debounceMs = 300,
  onJobTypeSelect: onSelect,
  skipQuery = false
}: UseListJobTypesAutocompleteParams = {}) => {
  return useAutocomplete<JobType>({
    searchTerm,
    debounceMs,
    onSelect,
    query: (search?: string) => {
      const { data, loading, error } = useListJobTypesQuery({
        variables: {
          skip: 0,
          where: {
            or: [
              {
                name: {
                  contains: search ?? searchTerm
                }
              }
            ]
          }
        },
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-first',
        skip: skipQuery
      });
      return {
        data: { items: data?.jobTypes?.items },
        loading: loading,
        error: error ?? null
      };
    },
    transform: (item) => ({
      value: item.id,
      label: item.name ?? 'Unknown Type'
    })
  });
};
